// @ts-check
const Joi = require('joi');

/** @type {import('../../types/misc').States } */
const validStates = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'ON',
  'PE',
  'QC',
  'SK',
  'NT',
  'NU',
  'YT',
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'AS',
  'GU',
  'MP',
  'PR',
  'UM',
  'VI',
];

const feeTypes = ['%', '$'];

const decimalStringPattern = /^-?\d+(\.\d{1,2})?$/;
const percentageStringPattern = /^-?\d+(\.\d{1,5})?$/;

// Should mimic FeeName type in fees.d.ts
/** @type {import('../../types/fees').FeeNames} */
const validFeeNames = ['tax', 'surcharge', 'lateFee', 'cardUpcharge', 'cashDiscount'];

/** @type {import('../../types/fees').PaymentTypes} */
const validPaymentTypes = ['credit', 'debit', 'ach', 'cash', ''];

const decimalStringSchema = Joi.string().pattern(decimalStringPattern).required();

const percentageStringSchema = Joi.string().pattern(percentageStringPattern).required();

const baseAmountSchema = decimalStringSchema.label('baseAmount');

const paymentTypeSchema = Joi.string()
  .valid(...validPaymentTypes)
  .optional();

const referenceNumberSchema = Joi.string().required().label('referenceNumber');

const stateSchema = Joi.string()
  .valid(...validStates)
  .required();

const surchargeByStateRateSchema = Joi.object({
  state: stateSchema,
  application: Joi.boolean().required(),
  type: Joi.string()
    .valid(...feeTypes)
    .allow(''),
}).keys({
  amount: Joi.alternatives().conditional('type', [
    { is: '%', then: Joi.string().pattern(percentageStringPattern).allow('') },
    { is: '$', then: Joi.string().pattern(decimalStringPattern).allow('') },
    { is: '', then: Joi.string().allow('') },
  ]),
});

const surchargeByStateRatesSchema = Joi.object().pattern(
  Joi.string().valid(...validStates),
  surchargeByStateRateSchema,
);

const feeOptionsSchema = Joi.object({
  dualPricing: Joi.boolean().optional(),
  taxLast: Joi.boolean().optional(),
  viewAllPrices: Joi.boolean().optional(),
  lateFeeEffectiveDate: Joi.date().optional(),
  surchargeByState: Joi.object({
    enabled: Joi.boolean().required(),
    rates: surchargeByStateRatesSchema,
  }).optional(),
}).optional();

/** Input for the service */
const feeInputSchema = Joi.object({
  enabled: Joi.boolean().required(),
  name: Joi.string()
    .valid(...validFeeNames)
    .optional(),
  type: Joi.string()
    .valid(...feeTypes)
    .required(),
  value: Joi.alternatives().conditional('type', [
    { is: '%', then: Joi.string().pattern(percentageStringPattern).required() },
    { is: '$', then: Joi.string().pattern(decimalStringPattern).required() },
  ]),
}).required();

const feeSchema = Joi.object({
  enabled: Joi.boolean().optional(),
  name: Joi.string()
    .valid(...validFeeNames)
    .optional(),
  type: Joi.string()
    .valid(...feeTypes)
    .required(),
  value: Joi.object({
    calculated: Joi.alternatives().conditional('...type', [
      { is: '%', then: Joi.string().pattern(percentageStringPattern).required() },
      { is: '$', then: Joi.string().pattern(decimalStringPattern).required() },
    ]),
    stored: Joi.alternatives().conditional('...type', [
      { is: '%', then: Joi.string().pattern(percentageStringPattern).required() },
      { is: '$', then: Joi.string().pattern(decimalStringPattern).required() },
    ]),
  }).required(),
  amount: Joi.string().optional(),
  display: Joi.boolean().optional(),
}).required();

const feeConfigSchema = Joi.object({
  cardUpcharge: feeInputSchema.required(),
  surcharge: feeInputSchema.required(),
  lateFee: feeInputSchema.required(),
  tax: feeInputSchema.required(),
}).required();

const feeArraySchema = Joi.array().required().label('Fees');

module.exports = {
  paymentTypeSchema,
  baseAmountSchema,
  feeSchema,
  feeInputSchema,
  feeArraySchema,
  feeOptionsSchema,
  feeConfigSchema,
  referenceNumberSchema,
  decimalStringSchema,
  percentageStringSchema,
  stateSchema,
};
